import { type Component, Show, batch, createSignal, onCleanup, onMount } from "solid-js";
import { type PulledQuote, getShuffledQuotes } from "../search/episodes";

const DISPLAY_MS = 9000;
const FADE_MS = 600;

/**
 * Cycles through randomly shuffled quotes from across the show.
 * Each quote sits visible for ~9 s; transitions are a 600 ms fade + slight rise.
 *
 * Visibility model: `.quote` is visible by default; we only add a
 * `.fading` class during the transition window. This avoids the
 * fragile "render-then-flip-visible-on-RAF" pattern that doesn't work
 * reliably after async work in onMount.
 */
export const QuoteRotator: Component = () => {
  const [quotes, setQuotes] = createSignal<PulledQuote[]>([]);
  const [current, setCurrent] = createSignal<PulledQuote | null>(null);
  const [fading, setFading] = createSignal(false);
  let cycleTimer: ReturnType<typeof setTimeout> | null = null;
  let fadeTimer: ReturnType<typeof setTimeout> | null = null;
  let idx = 0;

  function scheduleNext() {
    cycleTimer = setTimeout(() => {
      setFading(true);
      fadeTimer = setTimeout(() => {
        const list = quotes();
        if (list.length === 0) return;
        idx = (idx + 1) % list.length;
        batch(() => {
          setCurrent(list[idx]);
          setFading(false);
        });
        scheduleNext();
      }, FADE_MS);
    }, DISPLAY_MS);
  }

  onMount(async () => {
    const all = await getShuffledQuotes();
    if (all.length === 0) return;
    setQuotes(all);
    idx = 0;
    setCurrent(all[0]);
    scheduleNext();
  });

  onCleanup(() => {
    if (cycleTimer) clearTimeout(cycleTimer);
    if (fadeTimer) clearTimeout(fadeTimer);
  });

  return (
    <div class="quote-rotator" aria-live="polite">
      <Show when={current()}>
        {(q) => (
          <figure classList={{ quote: true, fading: fading() }}>
            <blockquote class="quote-text">“{q().text}”</blockquote>
            <figcaption class="quote-attr">— {q().episodeTitle}</figcaption>
          </figure>
        )}
      </Show>
    </div>
  );
};
